import { useState, useEffect, useRef } from "react";
import { StreamingSimulator } from "./streamingSimulator";
import { graphToChoreography } from "./graph";
import type { FlowGraph } from "./graph";
import type { SimResult } from "./choreography";
import { marketCapSolFromState, pumpBondingProgressFromState } from "./pumpSimulationMath";

export function useStreamingEngine(graph: FlowGraph, isPlaying: boolean, speedMultiplier: number = 1) {
  const [simState, setSimState] = useState<SimResult | null>(null);
  const simulatorRef = useRef<StreamingSimulator | null>(null);
  const lastTickTimeRef = useRef<number>(0);
  const reqFrameRef = useRef<number | null>(null);

  useEffect(() => {
    // Reset simulator when graph changes
    const choreo = graphToChoreography(graph);
    simulatorRef.current = new StreamingSimulator(choreo);
    
    // Initial state
    const mcSol = marketCapSolFromState(simulatorRef.current.ctx.state);
    setSimState({
      prints: simulatorRef.current.ctx.prints,
      candles: simulatorRef.current.ctx.candles,
      markers: [],
      ledgers: [...simulatorRef.current.ctx.ledgers.values()],
      finalState: simulatorRef.current.ctx.state,
      marketCapSol: mcSol,
      marketCapUsd: mcSol * simulatorRef.current.ctx.solUsd,
      bondingProgress: Math.min(100, pumpBondingProgressFromState(simulatorRef.current.ctx.state) * 100),
      totalSolIn: simulatorRef.current.ctx.totalIn,
      totalSolOut: simulatorRef.current.ctx.totalOut,
      errors: []
    });
  }, [graph]);

  useEffect(() => {
    if (!isPlaying) {
      if (reqFrameRef.current) cancelAnimationFrame(reqFrameRef.current);
      return;
    }

    lastTickTimeRef.current = performance.now();

    const loop = (time: number) => {
      const deltaRealMs = time - lastTickTimeRef.current;
      lastTickTimeRef.current = time;

      if (simulatorRef.current) {
        const deltaVirtualMs = deltaRealMs * speedMultiplier;
        const { newPrints, newCandles } = simulatorRef.current.tick(deltaVirtualMs);
        
        if (newPrints.length > 0 || newCandles.length > 0) {
          const sim = simulatorRef.current;
          const mcSol = marketCapSolFromState(sim.ctx.state);
          
          setSimState({
            prints: [...sim.ctx.prints],
            candles: [...sim.ctx.candles],
            markers: [],
            ledgers: [...sim.ctx.ledgers.values()],
            finalState: sim.ctx.state,
            marketCapSol: mcSol,
            marketCapUsd: mcSol * sim.ctx.solUsd,
            bondingProgress: Math.min(100, pumpBondingProgressFromState(sim.ctx.state) * 100),
            totalSolIn: sim.ctx.totalIn,
            totalSolOut: sim.ctx.totalOut,
            errors: []
          });
        }
      }
      
      reqFrameRef.current = requestAnimationFrame(loop);
    };

    reqFrameRef.current = requestAnimationFrame(loop);
    return () => {
      if (reqFrameRef.current) cancelAnimationFrame(reqFrameRef.current);
    };
  }, [isPlaying, speedMultiplier]);

  return simState;
}
